import { useState } from 'react';
import { useCreatePlant, useUpdatePlant, useOrganizations } from '../../../../hooks';

import { SetupDrawer } from '../../../atoms';
import { TextInput, Select } from '@pella/sash-framework';

import * as classes from './PlantDrawer.module.css';

const WMS_CODE_LENGTH = 3;

/**
 * Component for the Plant Drawer.
 * @param {Local.SetupDrawerProps} props - Component props
 */
export const PlantDrawer = ({ open, close, data, selectedId }) => {
  const { mutate: createPlant, isPending: isCreating } = useCreatePlant();
  const { mutate: updatePlant, isPending: isUpdating } = useUpdatePlant();
  const { data: organizations } = useOrganizations();

  const [formChanges, setFormChanges] = useState(
    /** @type {Partial<Pella.ProductionRequestAPI.CreatePlantInput>} */ ({}),
  );

  const currentForm = selectedId ? data?.find((record) => record.id === selectedId) : { active: true };

  // Combine initial data with form changes to get the current plant state
  const plant = { ...currentForm, ...formChanges };
  const isEdit = Boolean(selectedId);

  /** Handler to reset form and close drawer */
  const handleClose = () => {
    setFormChanges({});
    close();
  };

  /** handler for form submission */
  const handleSubmit = () => {
    // Ensure all required fields are defined before mutation
    if (!isEdit) {
      const input = {
        /* v8 ignore start */
        active: plant.active ?? true,
        description: plant.description || '',
        organizationId: plant.organizationId || 0,
        /* v8 ignore stop */
        wmsOrgCode: plant.wmsOrgCode || undefined,
      };
      createPlant(input, { onSuccess: handleClose });
    } else {
      const id = selectedId;
      const input = {
        description: plant.description,
        organizationId: plant.organizationId,
        wmsOrgCode: plant.wmsOrgCode || null,
        active: plant.active,
      };
      updatePlant({ id, input }, { onSuccess: handleClose });
    }
  };

  const hasRequiredFields = Boolean(plant.description?.trim() && plant.organizationId);
  const hasValidWmsCode = !plant.wmsOrgCode || plant.wmsOrgCode.length === WMS_CODE_LENGTH;
  const isLoading = isCreating || isUpdating;

  const disableSubmit = !hasRequiredFields || !hasValidWmsCode || isLoading;
  const disableForms = !plant.active;

  return (
    <SetupDrawer
      open={open}
      close={handleClose}
      title='Plant'
      isEdit={isEdit}
      isCopy={false}
      active={Boolean(plant.active)}
      onActiveChange={() => setFormChanges((prev) => ({ ...prev, active: !plant.active }))}
      onSubmit={handleSubmit}
      disableSubmit={disableSubmit}
    >
      <TextInput
        id='plantNameInput'
        labelText='Plant Name'
        disabled={disableForms}
        maxLength={50}
        required={Boolean(!plant.description)}
        value={plant.description || ''}
        onChange={(e) => setFormChanges((prev) => ({ ...prev, description: e.target.value }))}
      />
      <div className={classes.inputFields}>
        <Select
          id='organizationSelect'
          labelText='Organization'
          disabled={disableForms}
          required={Boolean(!plant.organizationId)}
          value={plant.organizationId || ''}
          onChange={(e) => setFormChanges((prev) => ({ ...prev, organizationId: Number(e.target.value) }))}
        >
          <option value='' disabled>
            Select an organization
          </option>
          {organizations?.map((org) => (
            <option key={org.organizationId} value={org.organizationId}>
              {org.description}
            </option>
          ))}
        </Select>
        <TextInput
          className={classes.wmsOrgCodeInput}
          id='wmsOrgCodeInput'
          labelText='WMS Org Code'
          disabled={disableForms}
          required={!hasValidWmsCode}
          maxLength={3}
          value={plant.wmsOrgCode || ''}
          onChange={(e) => setFormChanges((prev) => ({ ...prev, wmsOrgCode: e.target.value }))}
        />
      </div>
    </SetupDrawer>
  );
};
